/**
 * KnowledgeUpload — Karan uploads a folder → Kiaan sorts it into groups → Karan
 * checks who may see each group → saves (kiaan-create-agent-v2.html §3).
 *
 * Server does the sorting (Task 9 `sortFolder`): each file is STATE or EVENT;
 * duplicates land in group `old_duplicate` with no access. This screen only
 * lets the admin check and change the role tags before saving.
 */
import { useMemo, useRef, useState } from 'react';
import { KIAAN_UNREACHABLE, saveKnowledgeGroups, sortKnowledgeFolder, type RefusedFile, type SortedGroup } from '../kiaanApi';

export const OLD_DUPLICATE_KEY = 'old_duplicate';
const DEFAULT_ROLES = ['Sales', 'Production', 'Accounts', 'HR', 'Sales head', 'Everyone (summary)'];

type Phase = 'pick' | 'reading' | 'check' | 'saving' | 'saved';

/** `onDone(importId)` — after a save the caller opens the graph filtered to that import (SUN-78). */
export function KnowledgeUpload({ roles = DEFAULT_ROLES, onDone }: { roles?: string[]; onDone: (importId?: string | null) => void }) {
  const [phase, setPhase] = useState<Phase>('pick');
  const [folderName, setFolderName] = useState('');
  const [fileCount, setFileCount] = useState(0);
  const [groups, setGroups] = useState<SortedGroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(0);
  const [refusedByCheck, setRefusedByCheck] = useState(0);
  const [importId, setImportId] = useState<string | null>(null);
  const [refused, setRefused] = useState<RefusedFile[]>([]);
  const [adding, setAdding] = useState<string | null>(null); // group key with the role picker open
  const inputRef = useRef<HTMLInputElement | null>(null);

  const allRoles = useMemo(() => Array.from(new Set([...roles, ...groups.flatMap((g) => g.suggestedRoles)])), [roles, groups]);

  const onFiles = async (list: FileList | null) => {
    const files = Array.from(list ?? []);
    if (files.length === 0) return;
    const first = files[0] as File & { webkitRelativePath?: string };
    setFolderName(first.webkitRelativePath?.split('/')[0] || 'Your folder');
    setFileCount(files.length);
    setError(null);
    setPhase('reading');
    try {
      const r = await sortKnowledgeFolder(files);
      setGroups(r.groups);
      setRefused(r.refused ?? []);
      setPhase('check');
    } catch {
      setError(KIAAN_UNREACHABLE);
      setPhase('pick');
    }
  };

  const toggleRole = (key: string, role: string) => {
    setGroups((gs) => gs.map((g) => {
      if (g.key !== key) return g;
      const has = g.suggestedRoles.includes(role);
      return { ...g, suggestedRoles: has ? g.suggestedRoles.filter((r) => r !== role) : [...g.suggestedRoles, role] };
    }));
    setAdding(null);
  };

  const save = async () => {
    if (phase !== 'check') return;
    setPhase('saving');
    setError(null);
    try {
      const r = await saveKnowledgeGroups(groups);
      setSaved(r.saved ?? groups.length);
      setRefusedByCheck(r.rejected?.length ?? 0);
      setImportId(r.importId ?? null);
      setPhase('saved');
    } catch {
      setError(KIAAN_UNREACHABLE);
      setPhase('check');
    }
  };

  const kinds = (g: SortedGroup) => {
    const st = g.files.filter((f) => f.kind === 'STATE').length;
    const ev = g.files.length - st;
    return `${st} state · ${ev} event`;
  };

  return (
    <div className="kw-grid" style={{ gridTemplateColumns: '1fr 1.4fr' }} data-testid="knowledge-upload">
      <div className="kw-box">
        <h4>Your folder</h4>
        <div className="kw-m kw-me" style={{ maxWidth: '100%' }}>This is my folder with all our context. Add it to my knowledge.</div>
        {phase === 'pick' && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              // @ts-expect-error — non-standard but what every browser uses for folder pick
              webkitdirectory=""
              directory=""
              aria-label="Pick a folder"
              style={{ display: 'none' }}
              onChange={(e) => void onFiles(e.target.files)}
            />
            <button type="button" className="kw-btn" onClick={() => inputRef.current?.click()}>Pick a folder</button>
            <button type="button" className="kw-btn kw-light" onClick={() => onDone(null)}>Back</button>
            <div className="kw-small" style={{ marginTop: 6 }}>Kiaan reads every file, marks it as current truth or a dated event, and sorts it into groups.</div>
          </>
        )}
        {phase !== 'pick' && (
          <>
            <div className="kw-row">📁 {folderName} <span className="kw-pill kw-ok kw-ml">{fileCount} files</span></div>
            <div className="kw-row">
              {phase === 'reading' ? `Reading ${fileCount} files…` : `Read ${fileCount} of ${fileCount}`}
              <span className={`kw-pill kw-ml ${phase === 'reading' ? 'kw-wip' : 'kw-ok'}`}>{phase === 'reading' ? 'working' : 'done'}</span>
            </div>
          </>
        )}
        {(phase === 'check' || phase === 'saving') && (
          <div className="kw-m kw-bot" style={{ maxWidth: '100%' }} role="status">
            I sorted it into {groups.length} {groups.length === 1 ? 'group' : 'groups'}. Please check who should see each group.
          </div>
        )}
        {phase === 'saved' && (
          <>
            <div className="kw-m kw-bot" style={{ maxWidth: '100%' }} role="status">Saved {saved} {saved === 1 ? 'group' : 'groups'} to knowledge. Agents only read groups they are allowed.</div>
            {refusedByCheck > 0 && (
              <div className="kw-banner" role="alert" style={{ margin: '6px 0 0' }}>
                {refusedByCheck} {refusedByCheck === 1 ? 'file was' : 'files were'} not saved: the automatic check found a problem. See Knowledge → Rejected.
              </div>
            )}
            <button type="button" className="kw-btn" onClick={() => onDone(importId)}>{importId ? 'See what came in' : 'Done'}</button>
          </>
        )}
        {error && <div className="kw-banner kw-danger" role="alert" style={{ margin: '6px 0 0' }}>{error}</div>}
      </div>

      <div className="kw-box">
        <h4>Sorted knowledge (check and tag)</h4>
        {groups.length === 0 && <div className="kw-small">Groups show here after Kiaan reads the folder.</div>}
        {groups.map((g) => {
          const dup = g.key === OLD_DUPLICATE_KEY;
          return (
            <div key={g.key} className="kw-row" style={{ flexWrap: 'wrap' }} data-testid={`group-${g.key}`}>
              <b>{g.label}</b> · {g.files.length} files <span className="kw-small">({kinds(g)})</span>
              <span style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginLeft: 4 }}>
                {dup ? (
                  <span className="kw-chip">nobody</span>
                ) : (
                  g.suggestedRoles.map((r) => (
                    <button key={r} type="button" className="kw-chip" style={{ cursor: 'pointer' }} title="Click to remove" onClick={() => toggleRole(g.key, r)} disabled={phase !== 'check'}>
                      {r} ×
                    </button>
                  ))
                )}
                {!dup && phase === 'check' && (
                  adding === g.key ? (
                    <select className="kw-fld" style={{ width: 'auto', margin: 0, padding: '1px 4px', fontSize: 11 }} aria-label={`Add a role to ${g.label}`} defaultValue="" onChange={(e) => e.target.value && toggleRole(g.key, e.target.value)}>
                      <option value="">pick a role</option>
                      {allRoles.filter((r) => !g.suggestedRoles.includes(r)).map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : (
                    <button type="button" className="kw-chip" style={{ cursor: 'pointer' }} onClick={() => setAdding(g.key)} aria-label={`Add a role to ${g.label}`}>＋</button>
                  )
                )}
              </span>
              <span className={`kw-pill kw-ml ${dup ? 'kw-bad' : g.suggestedRoles.length > 0 ? 'kw-ok' : 'kw-wt'}`}>
                {dup ? 'skip' : g.suggestedRoles.length > 0 ? 'ok' : 'check'}
              </span>
            </div>
          );
        })}
        {(phase === 'check' || phase === 'saving') && (
          <>
            <button type="button" className="kw-btn" onClick={() => void save()} disabled={phase === 'saving'}>{phase === 'saving' ? 'Saving…' : 'Save to knowledge'}</button>
            <button type="button" className="kw-btn kw-light" onClick={() => { setGroups([]); setPhase('pick'); if (inputRef.current) inputRef.current.value = ''; }} disabled={phase === 'saving'}>Change folder</button>
          </>
        )}
        {refused.length > 0 && (
          <div className="kw-row" style={{ flexWrap: 'wrap' }} data-testid="refused">
            <b>Never imported</b> · {refused.length} {refused.length === 1 ? 'file' : 'files'}
            <span className="kw-pill kw-bad kw-ml">skip</span>
            <div className="kw-small" style={{ width: '100%' }}>{refused.map((f) => `${f.path} (${f.reason})`).join(' · ')}</div>
          </div>
        )}
        <div className="kw-small" style={{ marginTop: 6 }}>Stored on this server only. Agents only read groups they are allowed. Old or duplicate files get no access.</div>
      </div>
    </div>
  );
}
